import {useCallback, useMemo} from "react";
import {Trans, useTranslation} from "react-i18next";
import {useRouteMatch} from "react-router-dom";

import {IMonthCityStats} from "../../../../../api/src/db_queries/month_city_stats_query";
import {IMonthCityTypeStats} from "../../../../../api/src/db_queries/month_city_type_stats_query";
import {getCityMapRegionParams, getCityOption} from "../../../../../config/cities";
import {mapCitySlugToRegionType} from "../../../../../config/regions";
import {getRecentQuartersNames, lastFinishedQuarter, originDate} from "../../../../../utils/dates";
import {useAppDispatch, useAppSelector} from "../../../../../utils/hooks/store_hooks";
import {mapPathnameToOfferType, OfferType} from "../../../common/app/routing/offer_type";
import {RatioBarChartCard} from "../../../components/charts/chart_cards/BarChartCard";
import {RatioColumnStackedChartCard} from "../../../components/charts/chart_cards/ColumnStackedChartCard";
import {RatioLineColumnChartCard} from "../../../components/charts/chart_cards/LineColumnChartCard";
import {MultilineChartCard, RatioMultilineChartCard} from "../../../components/charts/chart_cards/MultilineChartCard";
import {PieBarChartCard, RatioPieBarChartCard} from "../../../components/charts/chart_cards/PieBarChartCard";
import {Grid} from "../../../components/Grid";
import {withDateGuard} from "../../../components/hoc/withDateGuard";
import {withGTM} from "../../../components/hoc/withGTM";
import {ChartsLayout as Layout} from "../../../components/layout/charts_layout/ChartsLayout";
import {
    clearChartsRegionPoc,
    selectChartsSelectedRegion,
    setChartsSelectedRegion
} from "../../../components/map/redux/map_city_poc_slice";
import {IOsmDistrictPolygon} from "../../../components/map/types/osm_district_polygon";
import {
    buildDistrictDrillDownSelection,
    buildMapParentRegionSnapshot,
    restoreParentRegionSelection
} from "../../../components/map/utils/district_map_drill_down";
import {toCityOutlineOsmMapParams} from "../../../components/map/utils/osm_map_region_params";
import {selectAuthStatus} from "../../auth/auth_slice";
import {selectLatestEntry} from "../../ssr/redux/latest_entry_slice";
import {pageviewAlgolyticsHit} from "../../tracking/algolytics/pageview_hits";
import {usePageview} from "../../tracking/use_pageview";
import {ViewType} from "../../tracking/view_type";
import {
    useGetChartsMonthCityStatsQuery,
    useGetChartsMonthCityTypeStatsQuery,
    useGetChartsMonthDistrictStatsQuery,
    useGetChartsQuarterCityStatsQuery,
    useGetChartsQuarterCityTypeStatsQuery
} from "../api/charts_platform_api";
import {DistrictOsmMapCard} from "../components/DistrictOsmMapCard";
import {useChartsRegionId} from "../hooks/use_charts_region_id";
import {useEffectiveCitySlug} from "../hooks/use_effective_city_slug";
import {useSelectedRegionDisplay} from "../hooks/use_selected_region_display";
import {buildDistrictMapValuesBySlug, resolveMapMetric} from "../utils/build_district_map_values_by_slug";
import {mapRtkQueryStatusToRequestStatus} from "../utils/rtq_status_to_request_status";

export const Sold = withDateGuard(
    withGTM(() => {
        const {path} = useRouteMatch();
        const citySlug = useEffectiveCitySlug();
        const {isKnownCity, regionLabel} = useSelectedRegionDisplay();
        const regionType = mapCitySlugToRegionType(citySlug);
        usePageview(() => {
            pageviewAlgolyticsHit({citySlug, regionType, viewType: ViewType.SOLD_DETAILS});
        });
        const checkOfferType = mapPathnameToOfferType(path);
        const isFlatOfferType = checkOfferType === OfferType.FLAT;
        const {t, i18n} = useTranslation();
        const {currentDate} = useAppSelector(selectLatestEntry);
        const lastMonth = currentDate ?? "";
        const lastQuarter = lastFinishedQuarter(lastMonth);
        const {isLoggedIn, isAuthorized} = useAppSelector(selectAuthStatus);
        const shouldFetch = isLoggedIn && isAuthorized;
        const dispatch = useAppDispatch();
        const chartsSelectedRegion = useAppSelector(selectChartsSelectedRegion);
        const {regionId, districtChartsRegionId, effectiveCitySlug, hasChartsAccess, isRegionIdReady} =
            useChartsRegionId();
        const isPermitted = isAuthorized && hasChartsAccess;
        const chartsCitySlug = effectiveCitySlug ?? citySlug;
        const isAgglomerationView = getCityOption(chartsCitySlug).useAgglomerationApi;
        const routeMapParams = useMemo(
            () => getCityMapRegionParams(chartsCitySlug, regionId ?? undefined),
            [chartsCitySlug, regionId]
        );
        const baseOsmMapParams = useMemo(
            () =>
                chartsSelectedRegion != null
                    ? {
                          regionsMapSlug: chartsSelectedRegion.regionsMapSlug,
                          regionsMapParentId: chartsSelectedRegion.regionsMapParentId,
                          regionsMapRegionId: chartsSelectedRegion.regionsMapRegionId,
                          regionsMapChildType: chartsSelectedRegion.regionsMapChildType
                      }
                    : routeMapParams,
            [chartsSelectedRegion, routeMapParams]
        );
        const hasDistrictStatsMap = baseOsmMapParams.regionsMapParentId != null;
        const skipChartsFetch = !shouldFetch || !isRegionIdReady || regionId == null;

        const {
            data = [],
            isError,
            isSuccess
        } = useGetChartsMonthCityStatsQuery(
            {
                region_id: regionId!,
                date_start: originDate,
                date_end: lastMonth,
                scenario: "sold"
            },
            {skip: skipChartsFetch}
        );
        const {
            data: typeData = [],
            isError: isTypeDataError,
            isSuccess: isTypeDataSuccess
        } = useGetChartsMonthCityTypeStatsQuery(
            {
                region_id: regionId!,
                date_start: originDate,
                date_end: lastMonth,
                offer_type: "Mieszkania",
                scenario: "sold"
            },
            {skip: skipChartsFetch}
        );

        const {data: monthDistrictData = []} = useGetChartsMonthDistrictStatsQuery(
            {
                region_id: districtChartsRegionId!,
                date_start: lastMonth,
                date_end: lastMonth,
                scenario: "sold",
                ...(isFlatOfferType ? {offer_type: "Mieszkania"} : {})
            },
            {
                skip: skipChartsFetch || districtChartsRegionId == null || (isAgglomerationView && !hasDistrictStatsMap)
            }
        );

        const {
            data: quarterData = [],
            isError: isQuarterDataError,
            isSuccess: isQuarterDataSuccess
        } = useGetChartsQuarterCityStatsQuery(
            {
                region_id: regionId!,
                date_start: lastQuarter,
                date_end: lastQuarter,
                scenario: "sold"
            },
            {skip: skipChartsFetch}
        );

        const {
            data: quarterTypeData = [],
            isError: isQuarterTypeDataError,
            isSuccess: isQuarterTypeDataSuccess
        } = useGetChartsQuarterCityTypeStatsQuery(
            {
                region_id: regionId!,
                date_start: lastQuarter,
                date_end: lastQuarter,
                scenario: "sold",
                ...(isFlatOfferType && {offer_type: "Mieszkania"})
            },
            {skip: skipChartsFetch}
        );

        const isCityMultiDistrictView = baseOsmMapParams.regionsMapParentId != null;
        const showDistrictPolygons = isCityMultiDistrictView && monthDistrictData.length > 0;
        const osmMapParams = useMemo(
            () => (showDistrictPolygons ? baseOsmMapParams : toCityOutlineOsmMapParams(baseOsmMapParams)),
            [baseOsmMapParams, showDistrictPolygons]
        );

        const monthStatsData = useMemo(() => (Array.isArray(data) ? data : []), [data]);
        const monthTypeStatsData = useMemo(() => (Array.isArray(typeData) ? typeData : []), [typeData]);
        const dataStatus = mapRtkQueryStatusToRequestStatus(isError, isSuccess);
        const typeDataStatus = mapRtkQueryStatusToRequestStatus(isTypeDataError, isTypeDataSuccess);
        const quarterStatus = mapRtkQueryStatusToRequestStatus(isQuarterDataError, isQuarterDataSuccess);

        const monthData = monthStatsData.length > 0 ? [monthStatsData[monthStatsData.length - 1]] : [];
        const flatMonthData = monthTypeStatsData.length > 0 ? [monthTypeStatsData[monthTypeStatsData.length - 1]] : [];

        const districtsMapAvailable = Boolean(
            osmMapParams.regionsMapSlug ||
                osmMapParams.regionsMapParentId != null ||
                osmMapParams.regionsMapRegionId != null
        );
        const lastMapStats = isFlatOfferType ? monthTypeStatsData.at(-1) : monthStatsData.at(-1);
        const districtSoldBySlug = useMemo(
            () =>
                buildDistrictMapValuesBySlug({
                    districtsMapAvailable,
                    monthDistrictData,
                    chartsCitySlug,
                    districtMetric: (d) => d.sold,
                    cityLevelMetric: resolveMapMetric(lastMapStats?.sold),
                    cityLevelStatsRecord: lastMapStats,
                    extraSlugKeys: [osmMapParams.regionsMapSlug, chartsSelectedRegion?.slug]
                }),
            [
                chartsCitySlug,
                chartsSelectedRegion?.slug,
                districtsMapAvailable,
                lastMapStats,
                monthDistrictData,
                osmMapParams.regionsMapSlug
            ]
        );
        const districtDrillDownEnabled = showDistrictPolygons && monthDistrictData.length > 0;
        const districtStatsCitySlug = getCityOption(chartsCitySlug).requestCitySlug;
        const mapParentRegion = chartsSelectedRegion?.parentRegion;

        const handleDistrictPolygonClick = useCallback(
            (district: IOsmDistrictPolygon) => {
                if (
                    districtChartsRegionId == null ||
                    baseOsmMapParams.regionsMapParentId == null ||
                    chartsCitySlug == null
                ) {
                    return;
                }

                const parentLabel = chartsSelectedRegion?.label ?? t(`city.${citySlug}.nominative`);
                const parentSlug = chartsSelectedRegion?.slug ?? baseOsmMapParams.regionsMapSlug ?? "";

                dispatch(
                    setChartsSelectedRegion(
                        buildDistrictDrillDownSelection(
                            district,
                            chartsCitySlug,
                            buildMapParentRegionSnapshot({
                                isRouteCity: chartsSelectedRegion == null,
                                regionId: districtChartsRegionId,
                                slug: parentSlug,
                                label: parentLabel,
                                citySlug: chartsSelectedRegion?.citySlug ?? chartsCitySlug,
                                hasChartsAccess,
                                mapParams: baseOsmMapParams
                            })
                        )
                    )
                );

                window.userengage?.("event.[BD]_district_click", {
                    city: chartsCitySlug,
                    district: district.slug_district ?? district.slug
                });
            },
            [baseOsmMapParams, chartsCitySlug, chartsSelectedRegion, citySlug, dispatch, districtChartsRegionId, t]
        );

        const handleBackToMapParent = useCallback(() => {
            const parent = chartsSelectedRegion?.parentRegion;
            if (!parent) {
                return;
            }

            if (parent.isRouteCity) {
                dispatch(clearChartsRegionPoc());
            } else {
                dispatch(setChartsSelectedRegion(restoreParentRegionSelection(parent)));
            }
        }, [chartsSelectedRegion, dispatch]);
        const quarterLabels = getRecentQuartersNames(new Date(lastQuarter), 8);
        const pieQuarterLabels = [quarterLabels[0], ...quarterLabels].map(
            (label, i) => `${t("sold.construction_started")} ${i === 0 ? t("common.before") : t("common.in")} ${label}`
        );
        const barQuarterLabels = [quarterLabels[0], ...quarterLabels].map(
            (label, i) => `${i === 0 ? t("common.before") : t("common.in")} ${label}`
        );

        const finalData = isFlatOfferType
            ? monthStatsData.map((item: IMonthCityStats, index: number) => ({
                  ...item,
                  sold: monthTypeStatsData[index] ? monthTypeStatsData[index].sold : item.sold
              }))
            : monthStatsData;

        return (
            <Layout>
                <Grid type={districtsMapAvailable ? "subpage" : "subpageMapless"}>
                    <RatioLineColumnChartCard<IMonthCityStats>
                        area="chart1"
                        chartId="sold-chart1"
                        title={<Trans>{t("sold.chart1.title")}</Trans>}
                        helper={<Trans>{t("sold.chart1.description")}</Trans>}
                        data={finalData}
                        dataStatus={isFlatOfferType ? typeDataStatus : dataStatus}
                        columnFields={[
                            "sold_studio",
                            "sold_2_rooms",
                            "sold_3_rooms",
                            "sold_4_plus_rooms",
                            ...(isFlatOfferType ? [] : ["sold_house"])
                        ]}
                        lineField="sold"
                        locked={isRegionIdReady && !isPermitted}
                        empty={!isRegionIdReady}
                    />

                    <RatioBarChartCard<IMonthCityStats | IMonthCityTypeStats>
                        area="chart2"
                        chartId="sold-chart2"
                        title={<Trans>{t("sold.chart2.title")}</Trans>}
                        helper={<Trans>{t("sold.chart2.description")}</Trans>}
                        data={isFlatOfferType ? flatMonthData : monthData}
                        dataStatus={isFlatOfferType ? typeDataStatus : dataStatus}
                        fields={[
                            "sold_offers_to_6k",
                            "sold_offers_between_6k_and_7k",
                            "sold_offers_between_7k_and_8k",
                            "sold_offers_between_8k_and_9k",
                            "sold_offers_between_9k_and_10k",
                            "sold_offers_between_10k_and_12k",
                            "sold_offers_between_12k_and_15k",
                            "sold_offers_between_15k_and_20k",
                            "sold_offers_above_20k"
                        ]}
                        height={225}
                        locked={isRegionIdReady && !isPermitted}
                        empty={!isRegionIdReady}
                    />

                    <RatioPieBarChartCard
                        area="chart3"
                        chartId="sold-chart3"
                        title={<Trans>{t("sold.chart3.title")}</Trans>}
                        helper={t("sold.chart3.description")}
                        pieData={isFlatOfferType ? flatMonthData : monthData}
                        pieStatus={isFlatOfferType ? typeDataStatus : dataStatus}
                        pieFields={[
                            "sold_offers_delivery_deadline_ready",
                            "sold_offers_delivery_deadline_in_3_months",
                            "sold_offers_delivery_deadline_in_6_months",
                            "sold_offers_delivery_deadline_in_1_year",
                            "sold_offers_delivery_deadline_in_2_year",
                            "sold_offers_delivery_deadline_above_2_years"
                        ]}
                        pieInnerTitle={t("sold.sold_offers")}
                        pieTooltipLabel="offer"
                        barData={isFlatOfferType ? flatMonthData : monthData}
                        barStatus={isFlatOfferType ? typeDataStatus : dataStatus}
                        barFields={[
                            "sold_offers_delivery_deadline_ready",
                            "sold_offers_delivery_deadline_in_3_months",
                            "sold_offers_delivery_deadline_in_6_months",
                            "sold_offers_delivery_deadline_in_1_year",
                            "sold_offers_delivery_deadline_in_2_year",
                            "sold_offers_delivery_deadline_above_2_years"
                        ]}
                        barMulticolorSet="alternateLong"
                        locked={isRegionIdReady && !isPermitted}
                        empty={!isRegionIdReady}
                    />

                    <MultilineChartCard<IMonthCityStats>
                        area="chart4"
                        chartId="sold-chart4"
                        title={<Trans>{t("sold.chart4.title")}</Trans>}
                        helper={t("sold.chart4.description")}
                        data={monthStatsData}
                        dataStatus={dataStatus}
                        fields={[
                            "sold_avg_area_studio",
                            "sold_avg_area_2_rooms",
                            "sold_avg_area_3_rooms",
                            "sold_avg_area_4_plus_rooms"
                        ]}
                        unit=" m<sup>2</sup>"
                        locked={isRegionIdReady && !isPermitted}
                        empty={!isRegionIdReady}
                    />

                    <RatioMultilineChartCard<IMonthCityStats | IMonthCityTypeStats>
                        area="chart5"
                        chartId="sold-chart5"
                        title={<Trans>{t("sold.chart5.title")}</Trans>}
                        helper={t("sold.chart5.description")}
                        data={isFlatOfferType ? monthTypeStatsData : monthStatsData}
                        dataStatus={isFlatOfferType ? typeDataStatus : dataStatus}
                        fields={[
                            "sold_area_to_30m2",
                            "sold_area_between_30_and_50m2",
                            "sold_area_between_50_and_70m2",
                            "sold_area_between_70_and_100m2",
                            "sold_area_between_100_and_120m2",
                            "sold_area_above_120m2"
                        ]}
                        unit={` ${t("common.sold", {context: "male", count: 5})}`}
                        locked={isRegionIdReady && !isPermitted}
                        empty={!isRegionIdReady}
                    />

                    <RatioColumnStackedChartCard
                        area="chart6"
                        chartId="sold-chart6"
                        title={<Trans>{t("sold.chart6.title")}</Trans>}
                        helper={<Trans>{t("sold.chart6.description")}</Trans>}
                        data={monthData}
                        dataStatus={dataStatus}
                        categories={[
                            "investment_type_intimate",
                            "investment_type_small",
                            "investment_type_medium",
                            "investment_type_large",
                            "investment_type_x_large",
                            ...(isFlatOfferType ? [] : ["investment_type_house"])
                        ]}
                        fieldGroups={[
                            [
                                "sold_investment_type_intimate_offer_studio",
                                "sold_investment_type_small_offer_studio",
                                "sold_investment_type_medium_offer_studio",
                                "sold_investment_type_large_offer_studio",
                                "sold_investment_type_x_large_offer_studio",
                                ...(isFlatOfferType ? [] : ["sold_houses_to_100m2"])
                            ],
                            [
                                "sold_investment_type_intimate_offer_2_rooms",
                                "sold_investment_type_small_offer_2_rooms",
                                "sold_investment_type_medium_offer_2_rooms",
                                "sold_investment_type_large_offer_2_rooms",
                                "sold_investment_type_x_large_offer_2_rooms",
                                ...(isFlatOfferType ? [] : ["sold_houses_between_100_and_150m2"])
                            ],
                            [
                                "sold_investment_type_intimate_offer_3_rooms",
                                "sold_investment_type_small_offer_3_rooms",
                                "sold_investment_type_medium_offer_3_rooms",
                                "sold_investment_type_large_offer_3_rooms",
                                "sold_investment_type_x_large_offer_3_rooms",
                                ...(isFlatOfferType ? [] : ["sold_houses_between_150_and_200m2"])
                            ],
                            [
                                "sold_investment_type_intimate_offer_4_plus_rooms",
                                "sold_investment_type_small_offer_4_plus_rooms",
                                "sold_investment_type_medium_offer_4_plus_rooms",
                                "sold_investment_type_large_offer_4_plus_rooms",
                                "sold_investment_type_x_large_offer_4_plus_rooms",
                                ...(isFlatOfferType ? [] : ["sold_houses_between_200_and_250m2"])
                            ],
                            ["", "", "", "", "", ...(isFlatOfferType ? [] : ["sold_houses_above_250m2"])]
                        ]}
                        minValue={1}
                        locked={isRegionIdReady && !isPermitted}
                        empty={!isRegionIdReady}
                    />
                    {/*handle one case, we do not have agglomeration data for the type of flat*/}
                    {!(isAgglomerationView && isFlatOfferType) && (
                        <PieBarChartCard
                            area="chart7"
                            chartId="sold-chart7"
                            title={
                                <Trans>
                                    {t(isFlatOfferType ? "sold.chart7.title_flats" : "sold.chart7.title", {
                                        quarter: barQuarterLabels[barQuarterLabels.length - 1]
                                    })}
                                </Trans>
                            }
                            helper={t("sold.chart7.description")}
                            pieData={isFlatOfferType ? quarterTypeData : quarterData}
                            pieStatus={quarterStatus}
                            pieFields={[
                                "above_8q",
                                "between_7_and_8q",
                                "between_6_and_7q",
                                "between_5_and_6q",
                                "between_4_and_5q",
                                "between_3_and_4q",
                                "between_2_and_3q",
                                "between_1_and_2q",
                                "to_1q"
                            ]}
                            pieInnerTitle={t("sold.sold_offers")}
                            pieTooltipLabel={undefined}
                            pieFieldsLabels={pieQuarterLabels}
                            barData={isFlatOfferType ? quarterTypeData : quarterData}
                            barStatus={quarterStatus}
                            barUnit={` ${t("common.pln_m2")}`}
                            barFields={[
                                "avg_price_8qplus",
                                "avg_price_8q",
                                "avg_price_7q",
                                "avg_price_6q",
                                "avg_price_5q",
                                "avg_price_4q",
                                "avg_price_3q",
                                "avg_price_2q",
                                "avg_price_1q"
                            ]}
                            barMulticolorSet="alternateLong"
                            barFieldsLabels={barQuarterLabels}
                            barTitle={` ${t("sold.construction_started")}`}
                            barHeight={225}
                            roundValues
                            locked={isRegionIdReady && !isPermitted}
                            empty={!isRegionIdReady}
                        />
                    )}

                    <DistrictOsmMapCard
                        chartId="sold-map"
                        title={
                            <Trans>
                                {isKnownCity
                                    ? t("sold.map.title", {city: chartsCitySlug})
                                    : t("sold.map.title_region", {region: regionLabel})}
                            </Trans>
                        }
                        helper={t("sold.map.description")}
                        visible={districtsMapAvailable}
                        sticky
                        citySlug={chartsCitySlug}
                        regionId={regionId}
                        regionsMapSlug={osmMapParams.regionsMapSlug}
                        regionsMapParentId={osmMapParams.regionsMapParentId}
                        regionsMapChildType={osmMapParams.regionsMapChildType}
                        regionsMapRegionId={osmMapParams.regionsMapRegionId}
                        districtsMapAvailable={districtsMapAvailable}
                        districtValuesBySlug={districtSoldBySlug}
                        districtStatsCitySlug={districtStatsCitySlug}
                        legendType="quantitative"
                        legendColors={["#FFE8EC", "#FFD4DC", "#FF98B1", "#FF6D99", "#FF4085"]}
                        metricLabel={t("fields.sold", {defaultValue: "Sprzedane"})}
                        locale={i18n.language}
                        locked={isRegionIdReady && !isPermitted}
                        empty={!isRegionIdReady}
                        cityOutlineMetric={!showDistrictPolygons}
                        districtDrillDownEnabled={districtDrillDownEnabled}
                        onDistrictPolygonClick={handleDistrictPolygonClick}
                        onBackToParentRegion={mapParentRegion ? handleBackToMapParent : undefined}
                        backToParentLabel={mapParentRegion ? `← ${mapParentRegion.label}` : undefined}
                    />
                </Grid>
            </Layout>
        );
    })
);
